import React, { useEffect, useMemo, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { getIsTaskPreSigning } from 'src/helpers';
import { useGetWorkflowTasks } from 'src/hooks';
import { useSendForSignatureWithChecks } from 'src/hooks/workflow/send-for-signature';
import { makeStyles } from 'tss-react/mui';

import { workflowQuickStartSteps } from './components/helpers';
import type { StepOption } from './components/WorkflowStepOptions';
import {
  SelectedWorkflowStep,
  StepOptionId,
  WorkflowStepOption,
  WorkflowStepOptionLoading,
} from './components/WorkflowStepOptions';
import { PageHeader } from './PageHeader';
import { StepLayout } from './StepLayout';
import { SanityCheckModal, WorkflowCancelDialog } from '../send-for-signature';

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
}));

export function PickContractWorkflowAction(): JSX.Element | null {
  const { workflowId } = useParams<{ workflowId: UUID }>();

  if (!workflowId) return null;

  return (
    <>
      <Helmet>
        <title>Workflow Quick Start</title>
      </Helmet>
      <PickworkflowActions activeStep={3} steps={workflowQuickStartSteps} workflowId={workflowId} />
    </>
  );
}

export default function PickworkflowActions({
  activeStep,
  steps,
  workflowId,
}: {
  activeStep: number;
  steps: number[];
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const {
    loading,
    openDialog,
    selectedStep,
    selectedStepOption,
    sendForSignatureIsSubmitting,
    setOpenDialog,
    setSelectedStep,
    stepOptions,
  } = usePickworkflowAction(workflowId);

  function getPrimaryActionTitle(): string {
    switch (selectedStep) {
      case StepOptionId.Send:
        return 'Start Signing';
      case StepOptionId.Invite:
        return 'Done';
      case StepOptionId.Save:
        return 'Go to Draft';
    }
  }

  return (
    <>
      <div className={classes.root}>
        <PageHeader
          activeStep={activeStep}
          back={() => navigate(-1)}
          next={() => selectedStepOption?.onContinue()}
          pageTitle="New workflow"
          primaryActionLoading={sendForSignatureIsSubmitting}
          primaryActionTitle={getPrimaryActionTitle()}
          steps={steps}
          onClose={() => setCancelOpen(true)}
        />
        <PickworkflowActionBody
          loading={loading}
          selectedStep={selectedStep}
          setSelectedStep={setSelectedStep}
          stepOptions={stepOptions}
          workflowId={workflowId}
        />
      </div>
      <WorkflowCancelDialog open={cancelOpen} setOpen={setCancelOpen} workflowId={workflowId} />
      <SanityCheckModal
        openDialogTuple={[openDialog, setOpenDialog]}
        workflowId={workflowId}
        onClickFixMissingActions={() => setOpenDialog(null)}
        onClose={() => setOpenDialog(null)}
      />
    </>
  );
}

const useBodyStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: theme.breakpoints.values.md,
  },
  customisedEmailContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  root: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  stepLayout: {
    width: '100%',
  },
  stepsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
}));

type PickworkflowActionBodyProps = {
  loading: boolean;
  selectedStep: StepOption['id'];
  setSelectedStep: (step: StepOption['id']) => void;
  stepOptions: StepOption[];
  workflowId: UUID;
};

function PickworkflowActionBody({
  loading,
  selectedStep,
  setSelectedStep,
  stepOptions,
  workflowId,
}: PickworkflowActionBodyProps) {
  const { classes } = useBodyStyles();

  if (loading) return <WorkflowStepOptionLoading />;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <StepLayout className={classes.stepLayout} title="Choose your next step">
          <div className={classes.stepsContainer}>
            {stepOptions.map(option => (
              <WorkflowStepOption
                key={option.id}
                option={option}
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
              />
            ))}
          </div>
        </StepLayout>
        <SelectedWorkflowStep selectedStep={selectedStep} workflowId={workflowId} />
      </div>
    </div>
  );
}

export function usePickworkflowAction(workflowId: UUID) {
  const navigate = useNavigate();
  const { loading, phases, tasks, workflow } = useGetWorkflowTasks(workflowId);
  const [selectedStep, setSelectedStep] = useState<StepOptionId>(StepOptionId.Send);
  const areSomePreSigTasksIncomplete = tasks.some(
    task => getIsTaskPreSigning(task, phases) && !task.completedAt
  );
  const {
    isSubmitting: sendForSignatureIsSubmitting,
    openDialog,
    sendForSignatureWithChecks,
    setOpenDialog,
  } = useSendForSignatureWithChecks({
    showNotReadyReasons: true,
    workflowId,
  });

  const stepOptions: StepOption[] = useMemo(
    () => [
      {
        dataCy: Select.components.quickStart.steps.sendForSignature,
        description: 'Send documents for electronic signature via AlineSign.',
        disabledReason: areSomePreSigTasksIncomplete
          ? 'Must complete all required tasks before sending for signature.'
          : '',
        id: StepOptionId.Send,
        onContinue: async () => {
          const { success } = await sendForSignatureWithChecks();
          if (success) navigate(link.WorkflowDetail({ workflowId }));
        },
        title: 'Send for signature',
      },
      {
        dataCy: Select.components.quickStart.steps.inviteReviewers,
        description: 'Get sign-offs, delegate work, or send for redlines.',
        disabledReason: '',
        id: StepOptionId.Invite,
        onContinue: () => {
          navigate(link.WorkflowDetail({ workflowId }));
        },
        title: 'Collaborate with internal or external parties',
      },
      {
        dataCy: Select.components.quickStart.steps.saveAsDraft,
        description: 'Keep working on the draft and I can send it out later.',
        disabledReason: '',
        id: StepOptionId.Save,
        onContinue: () => {
          navigate(link.WorkflowDetail({ workflowId }));
        },
        title: 'Save as draft',
      },
    ],
    [areSomePreSigTasksIncomplete, navigate, sendForSignatureWithChecks, workflowId]
  );
  const [selectedStepOption, setSelectedStepOption] = useState<StepOption | null>(
    stepOptions.find(option => option.id === selectedStep) ?? null
  );

  useEffect(() => {
    const option = stepOptions.find(option => option.id === selectedStep);
    if (option) setSelectedStepOption(option);
  }, [selectedStep, stepOptions]);
  useEffect(() => {
    if (areSomePreSigTasksIncomplete) setSelectedStep(StepOptionId.Invite);
  }, [areSomePreSigTasksIncomplete]);

  return {
    loading,
    openDialog,
    selectedStep,
    selectedStepOption,
    sendForSignatureIsSubmitting,
    setOpenDialog,
    setSelectedStep,
    stepOptions,
    workflow,
  };
}
